import React, { useCallback, useEffect, useState } from "react";
import Cropper from "react-easy-crop";
import { X, ZoomIn, ZoomOut, RotateCcw } from "lucide-react";

/**
 * Circular photo cropper. Loads the given File into an <img>, lets user
 * pan + zoom, and on save produces a 512×512 JPEG blob passed to onSave.
 */
export default function PhotoCropper({ file, onSave, onCancel, saving = false }) {
  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  useEffect(() => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setImageSrc(reader.result);
    reader.readAsDataURL(file);
  }, [file]);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  const onCropComplete = useCallback((_area, pixels) => setCroppedAreaPixels(pixels), []);

  const reset = () => { setCrop({ x: 0, y: 0 }); setZoom(1); };

  const handleSave = async () => {
    if (!imageSrc || !croppedAreaPixels) return;
    const blob = await getCroppedBlob(imageSrc, croppedAreaPixels);
    if (blob) onSave(blob);
  };

  return (
    <div className="fixed inset-0 z-[90] bg-black/90 backdrop-blur flex flex-col" data-testid="photo-cropper">
      <div className="flex items-center justify-between border-b border-white/10 px-4 sm:px-8 py-4">
        <div>
          <div className="font-mono text-[0.55rem] text-gold tracking-[0.32em] uppercase mb-1">Position & Zoom</div>
          <div className="font-display text-white text-base sm:text-lg font-light tracking-[-0.02em]">Center your photo</div>
        </div>
        <button onClick={onCancel} disabled={saving} className="text-white/60 hover:text-gold p-2 disabled:opacity-40" data-testid="cropper-close"><X className="w-5 h-5" /></button>
      </div>

      <div className="flex-1 relative bg-black">
        {imageSrc && (
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            aspect={1}
            cropShape="round"
            showGrid={false}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropComplete}
            objectFit="contain"
            style={{ containerStyle: { background: "#050a14" } }}
          />
        )}
      </div>

      <div className="border-t border-white/10 px-4 sm:px-8 py-5 space-y-4">
        <div className="flex items-center gap-3">
          <ZoomOut className="w-4 h-4 text-white/50 flex-shrink-0" />
          <input
            type="range"
            min={1}
            max={4}
            step={0.05}
            value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
            className="flex-1 accent-gold"
            data-testid="cropper-zoom"
          />
          <ZoomIn className="w-4 h-4 text-white/50 flex-shrink-0" />
        </div>
        <div className="flex flex-col sm:flex-row gap-3">
          <button onClick={reset} disabled={saving} className="btn-ghost-blue !py-3 !text-[0.62rem] inline-flex items-center justify-center gap-2 disabled:opacity-40" data-testid="cropper-reset">
            <RotateCcw className="w-3.5 h-3.5" /> Reset
          </button>
          <button onClick={onCancel} disabled={saving} className="btn-ghost-blue !py-3 !text-[0.62rem] flex-1 disabled:opacity-40" data-testid="cropper-cancel">
            Cancel
          </button>
          <button onClick={handleSave} disabled={saving || !croppedAreaPixels} className="btn-gold !py-3 !text-[0.62rem] flex-1 disabled:opacity-50" data-testid="cropper-save">
            {saving ? "Uploading…" : "Save Photo"}
          </button>
        </div>
        <p className="text-center text-ag-text-muted text-[0.62rem] font-light">Drag to reposition · pinch or slide to zoom</p>
      </div>
    </div>
  );
}

// ─── canvas helpers ───
function createImage(url) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = "anonymous";
    image.addEventListener("load", () => resolve(image));
    image.addEventListener("error", reject);
    image.src = url;
  });
}

async function getCroppedBlob(imageSrc, pixelCrop) {
  const image = await createImage(imageSrc);
  const output = 512;
  const canvas = document.createElement("canvas");
  canvas.width = output;
  canvas.height = output;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  ctx.drawImage(
    image,
    pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height,
    0, 0, output, output
  );
  return new Promise((resolve) => canvas.toBlob((b) => resolve(b), "image/jpeg", 0.92));
}
